// #tag: ADMIN USER MANAGEMENT SCREEN
// #purpose: Manages system user accounts, role assignments (super_admin, admin, cms, editor, student), account statuses, and detailed audit histories.
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Shield,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import {
  createAdminUser,
  fetchAdminMe,
  fetchAdminUser,
  fetchAdminUsers,
  patchAdminUser,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminEmptyState,
  AdminDetailCard,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { adminDetailIllustration, adminRoleIllustration } from '../../config/adminIllustrations';
import { LoadingState, ErrorState } from '../../components/states/StateViews';
import type { AdminRole, AdminUserRow } from '../../types/admin';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  cms: 'CMS',
  editor: 'Editor',
  student: 'Student',
};

const ALL_ROLES: AdminRole[] = ['super_admin', 'admin', 'cms', 'editor', 'student'];

function assignableRoles(actorRole: AdminRole | string | undefined): AdminRole[] {
  if (actorRole === 'super_admin') return ALL_ROLES;
  return ALL_ROLES.filter((role) => role !== 'super_admin');
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span className={`wfx-admin-role-badge role-${role}`}>
      {role === 'super_admin' && <Shield size={12} aria-hidden="true" />}
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}

function CreateUserModal({
  open,
  onClose,
  actorRole,
}: {
  open: boolean;
  onClose: () => void;
  actorRole?: AdminRole | string;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'student',
    status: 'active',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({ name: '', email: '', phone: '', password: '', role: 'student', status: 'active' });
    setError('');
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      createAdminUser({
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        password: form.password,
        role: form.role,
        status: form.status,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create user');
    },
  });

  if (!open) return null;

  const roles = assignableRoles(actorRole);

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-user-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">New account</p>
            <h2 id="create-user-title"><UserPlus size={20} /> Create user</h2>
          </div>
          <button type="button" className="wfx-admin-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Full name</span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Jane Doe"
            />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@example.com"
              />
            </label>
            <label className="wfx-admin-field">
              <span>Phone</span>
              <input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+91…"
              />
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Password</span>
            <input
              required
              type="password"
              minLength={6}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="Minimum 6 characters"
            />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Role</span>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                {roles.map((role) => (
                  <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                ))}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Status</span>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </label>
          </div>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserDetailDrawer({
  userId,
  onClose,
  actorRole,
  currentUserId,
}: {
  userId: string | null;
  onClose: () => void;
  actorRole?: AdminRole | string;
  currentUserId?: string;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => fetchAdminUser(userId!),
    enabled: Boolean(userId),
  });

  const update = useMutation({
    mutationFn: (body: { role?: string; status?: string }) => patchAdminUser(userId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      void qc.invalidateQueries({ queryKey: ['admin-user', userId] });
    },
  });

  const user = data?.user;
  const roles = assignableRoles(actorRole);
  const isSelf = user?.id === currentUserId;

  if (!userId) return null;

  return (
    <AdminDetailCard
      open={Boolean(userId)}
      onClose={onClose}
      ariaLabel="User details"
      breadcrumb="User profile"
      title={user?.name ?? 'Loading…'}
      illustration={user ? adminRoleIllustration(user.role) : adminDetailIllustration('user')}
      subtitle={user ? <RoleBadge role={user.role} /> : undefined}
    >
      {isLoading && <LoadingState label="Loading user…" />}
      {isError && <ErrorState message="Could not load user details." onRetry={() => void qc.invalidateQueries({ queryKey: ['admin-user', userId] })} />}
      {user && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Email</dt><dd>{user.email ?? '—'}</dd></div>
            <div><dt>Phone</dt><dd>{user.phone ?? '—'}</dd></div>
            <div><dt>Joined</dt><dd>{new Date(user.createdAt).toLocaleString()}</dd></div>
            <div><dt>Role</dt><dd><RoleBadge role={user.role} /></dd></div>
            <div><dt>Status</dt><dd><AdminStatusBadge status={user.status ?? 'active'} /></dd></div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Manage access</h3>
              <div className="wfx-admin-form-row">
                <label className="wfx-admin-field">
                  <span>Role</span>
                  <select
                    className="wfx-admin-select"
                    value={user.role}
                    disabled={isSelf || update.isPending}
                    onChange={(e) => update.mutate({ role: e.target.value })}
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                    ))}
                  </select>
                </label>
                <label className="wfx-admin-field">
                  <span>Status</span>
                  <select
                    className="wfx-admin-select"
                    value={user.status ?? 'active'}
                    disabled={isSelf || update.isPending}
                    onChange={(e) => update.mutate({ status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </label>
              </div>
              {isSelf && <p className="wfx-muted">You cannot change your own role or suspend yourself.</p>}
            </div>

            {data?.permissions && (
              <div className="wfx-admin-drawer-section">
                <h3>Permissions</h3>
                <div className="wfx-admin-permission-tags">
                  {data.permissions.map((perm) => (
                    <span key={perm} className="chip-pill">{perm}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function UserRowActions({
  user,
  actorRole,
  currentUserId,
  onSelect,
}: {
  user: AdminUserRow;
  actorRole?: AdminRole | string;
  currentUserId?: string;
  onSelect: () => void;
}) {
  const qc = useQueryClient();
  const roles = assignableRoles(actorRole);
  const isSelf = user.id === currentUserId;

  const update = useMutation({
    mutationFn: (body: { role?: string; status?: string }) => patchAdminUser(user.id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  return (
    <tr className="wfx-admin-table-row-clickable" onClick={onSelect}>
      <td><strong>{user.name}</strong></td>
      <td>{user.email ?? '—'}</td>
      <td>{user.phone ?? '—'}</td>
      <td onClick={(e) => e.stopPropagation()}>
        <select
          className="wfx-admin-select wfx-admin-select--compact"
          value={user.role}
          disabled={isSelf || update.isPending}
          aria-label={`Role for ${user.name}`}
          onChange={(e) => update.mutate({ role: e.target.value })}
        >
          {roles.map((role) => (
            <option key={role} value={role}>{ROLE_LABELS[role]}</option>
          ))}
        </select>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <select
          className="wfx-admin-select wfx-admin-select--compact"
          value={user.status ?? 'active'}
          disabled={isSelf || update.isPending}
          aria-label={`Status for ${user.name}`}
          onChange={(e) => update.mutate({ status: e.target.value })}
        >
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </td>
      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
    </tr>
  );
}

export function AdminUsersScreen() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(() => searchParams.get('role') || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    const role = searchParams.get('role');
    if (role) setRoleFilter(role);
  }, [searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, roleFilter, statusFilter]);

  const { data: me } = useQuery({
    queryKey: ['admin-me'],
    queryFn: fetchAdminMe,
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-users', debouncedQuery, roleFilter, statusFilter, page],
    queryFn: () =>
      fetchAdminUsers({
        q: debouncedQuery || undefined,
        role: roleFilter,
        status: statusFilter,
        page,
        limit: 20,
      }),
  });

  const stats = data?.stats;
  const pagination = data?.pagination;
  const users = data?.data ?? [];

  const kpiItems = useMemo(
    () => [
      { label: 'Total Users', value: stats?.total ?? 0 },
      {
        label: 'Active',
        value: stats?.active ?? 0,
      },
      {
        label: 'Staff',
        value: (stats?.byRole?.super_admin ?? 0)
          + (stats?.byRole?.admin ?? 0)
          + (stats?.byRole?.cms ?? 0)
          + (stats?.byRole?.editor ?? 0),
      },
      { label: 'Students', value: stats?.byRole?.student ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading users…" />;
  if (isError) return <ErrorState message="Could not load users." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / User Management"
        title="Users"
        description="Manage accounts, roles, and access across the WebFoxx ecosystem."
        icon={Users}
        illustration="admin.module.users"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            Create user
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <AdminTablePanel
          title="All accounts"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search name, email, phone…"
              />
              <select
                className="wfx-admin-select"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                aria-label="Filter by role"
              >
                <option value="all">All roles</option>
                {ALL_ROLES.map((role) => (
                  <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} users
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <UserRowActions
                  key={user.id}
                  user={user}
                  actorRole={me?.user.role}
                  currentUserId={me?.user.id}
                  onSelect={() => setSelectedUserId(user.id)}
                />
              ))}
            </tbody>
          </table>
          {users.length === 0 && <AdminEmptyState message="No users match your filters." />}
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        actorRole={me?.user.role}
      />

      <UserDetailDrawer
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        actorRole={me?.user.role}
        currentUserId={me?.user.id}
      />
    </>
  );
}
